import { useCallback, useEffect, useRef, useState } from "react";
import * as Cesium from "cesium";
import useGenericSimulator from "./useGenericSimulator";
import { createNaturalEarthProvider, offline } from "../../../utils/commonFunctions";

const useCesium = () => {
    const cesiumContainerRef = useRef<HTMLDivElement | null>(null);
    const { setViewer } = useGenericSimulator();
    const [mapViewer, setMapViewer] = useState<Cesium.Viewer | null>(null);

    const initViewer = useCallback(() => {
        if (!cesiumContainerRef.current) return;

        const Imaginary = new Cesium.MapboxStyleImageryProvider({
            styleId: 'dark-v10',
            accessToken: process.env.REACT_APP_MAPBOX_TOKEN!,
        });

        const cesiumViewer = new Cesium.Viewer(cesiumContainerRef.current, {
            requestRenderMode: true,
            maximumRenderTimeChange: Infinity,  // only render on explicit requestRender()
            shouldAnimate: true,
            animation: false,
            baseLayerPicker: false,
            timeline: false,
            geocoder: false,
            homeButton: false,
            navigationHelpButton: false,
            sceneModePicker: false,
            selectionIndicator: false,
            fullscreenButton: false,
            targetFrameRate: 30,
            imageryProvider: offline() ? createNaturalEarthProvider() : Imaginary,
            infoBox: false,
            shadows: false,
            skyBox: false,
            orderIndependentTranslucency: false,
            contextOptions: {
                webgl: {
                    antialias: false,
                },
            },
        });

        // Disable heavy post-processing effects
        cesiumViewer.scene.fog.enabled = false;
        cesiumViewer.scene.globe.showGroundAtmosphere = false;
        cesiumViewer.scene.globe.enableLighting = false;
        cesiumViewer.scene.postProcessStages.fxaa.enabled = false;
        cesiumViewer.scene.skyAtmosphere.show = false;

        cesiumViewer.scene.morphTo3D(0);

        setMapViewer(cesiumViewer);
        setViewer(cesiumViewer);

        setTimeout(() => {
            cesiumViewer.scene.camera.flyTo({
                destination: Cesium.Cartesian3.fromDegrees(78.9629, 20.5937, 25000000),
                duration: 2,
            });
        }, 500);

        return cesiumViewer;
    }, []);

    useEffect(() => {
        const viewer = initViewer();
        return () => {
            if (viewer && !viewer.isDestroyed()) {
                viewer.destroy();
            }
        };
    }, []);

    return {
        cesiumContainerRef,
        mapViewer,
    };
};

export default useCesium;
